<script setup>
    /**
     * Vue Scripts
     */
    import {computed, useTemplateRef} from "vue";
    import {toNumber} from "lodash";
    import {trialStore} from "@pinia/trial-store";
    import {getQueryParams} from "@util/api";
    import {objNotEmpty} from "@util/mixins";
    import {handlePinSelection, resetAllPins} from "@util/map";
    import {dataLayer, reifyPrescreener} from "@util/util";

    /**
     * Vue Components
     */
    import DirectionIcon from "@pages/Trial/TrialMap/Listing/Locations/Location/DirectionIcon/DirectionIcon.vue";
    import {globalStore} from "@pinia/global-store";

    const props = defineProps({
        address: {
            type: String,
            default: '',
        },
        coords: [Array, Object],
        directions: {
            type: String,
            default: '',
        },
        directionsColor: {
            type: String,
            default: '',
        },
        directionsColorHover: {
            type: String,
            default: '',
        },
        distance: [Number, String],
        facility: {
            type: String,
            default: '',
        },
        icon: {
            type: String,
            default: '',
        },
        index: [Number, String],
        phone: [Array, Object],
        reifyLocationId: String,
        titleColor: {
            type: String,
            default: '',
        },
        titleColorHover: {
            type: String,
            default: '',
        },
        units: {
            type: String,
            default: '',
        },
    })
    const store = trialStore()

    const locationRef = useTemplateRef('location')
    const {pin, pinSelected} = store.icons
    const sessionId = computed(() => globalStore().sessionId)
    const selectedLocation = computed(() => store.locationSelected)

    //region Flex Trial Colors
    let titleClass = [];
    let directionsClass = [];

    if (typeof TRIAL_COLORS !== 'undefined') {
        titleClass.push(TRIAL_COLORS.titleColor, TRIAL_COLORS.titleColorHover);
        directionsClass.push(TRIAL_COLORS.directionsColor, TRIAL_COLORS.directionsColorHover);
    }
    //endregion

    const {
        distanceUnits,
        getDirectionsText,
        locAntidote,
        locPrescreener,
        locPrescreenerBtn,
        newTabText,
        pageName,
        reifyId,
        reifyTrialId,
    } = TRIAL;
    const {locale} = GLOBAL;
    const queryParams = getQueryParams(new URL(window.location).search)
    const phoneHasData = () => objNotEmpty(props.phone);

    /**
     * Data to pass to the Reify Location Pre-Screener
     * @type {{locale: *, micrositeId: *, queryParams: {}, sessionId: *, trialId: *}}
     */
    const reifyData = {
        locale,
        micrositeId: reifyId,
        queryParams,
        sessionId: sessionId.value,
        trialId: reifyTrialId,
    }

    /**
     * Adds to the Google Analytics dataLayer based on what is clicked
     *
     * @param {String} event The event name
     * @param {Object} additionalData Any additional data to be passed
     */
    const dataLayerAction = (event, additionalData = {}) => {
        dataLayer(
            {
                event,
                trialName: pageName,
                facility: props.facility,
                ...additionalData,
                element: locationRef.value,
            }
        )
    }

    /**
     * Handles passing a GA event when a location is clicked
     * in addition to handling, updating the store state, and
     * changing the map pins to the selected option
     */
    const titleSelected = () => {
        const {facility, index} = props;
        if (selectedLocation.value && toNumber(selectedLocation.value) === index) return;

        store.updateLocationSelected(index);

        dataLayerAction('locationClicked')

        const mapDiv = document.querySelector('.mapdiv')
        if (!mapDiv) return

        const mapPin = mapDiv.querySelector(`[title="${facility}"]`)
        if (!mapPin) return

        resetAllPins(pin);
        handlePinSelection(mapPin, pinSelected)
    }
</script>

<template>
    <div
        :id="`location-${index}`"
        :data-facility="facility"

        class="location"
        :class="{'selected': index === (selectedLocation && toNumber(selectedLocation))}"
        ref="location"
    >
        <div class="header">
            <p
                v-if="facility"
                class="title"
                :class="titleClass.length && titleClass.join(' ')"
            >
                <button
                    type="button"
                    class="location-header-title__button"
                    @click="titleSelected"
                    v-html="facility"
                />
            </p>
            <div
                class="miles"
                :class="titleClass.length && titleClass.join(' ')"
            >
                <p
                    class="num"
                    v-text="distance"
                />
                <p
                    class="label"
                    v-html="distanceUnits"
                />
            </div>
        </div>

        <div
            v-if="address"
            class="address"
        >
            <p v-html="address"/>
        </div>

        <div class="row middle">

            <div
                v-if="phoneHasData"
                class="col-6"
            >
                <a
                    class="phone"
                    rel="nofollow"

                    :href="phone?.tel"
                    v-html="phone?.display"

                    @click="dataLayerAction('locationPhoneClicked', {phoneNumber: phone?.display || ''})"
                />
            </div>

            <div
                :class="{
                    'col-6': !phoneHasData,
                    'col-12 no-phone': phoneHasData,
                }"
            >
                <a
                    class="directions"
                    target="_blank"

                    :href="directions"
                    :class="directionsClass.length && directionsClass.join(' ')"

                    @click="dataLayerAction('locationDirectionsClicked', {directionsLink: directions})"
                >

                    <DirectionIcon/>

                    {{ getDirectionsText }}
                    <span
                        class="screen-reader-only"
                        v-html="newTabText"
                    />
                </a>
            </div>

        </div>

        <!-- Reify and OST -->
        <div
            v-if="props.reifyLocationId"
            class="row bottom"
        >
            <div
                v-if="locPrescreener"
                class="btn-container"
            >
                <button
                    class="btn-info btn-info-small"

                    :id="`prescreener-${reifyId}`"
                    v-html="locPrescreenerBtn"

                    @click="reifyPrescreener(reifyData, props.reifyLocationId)"
                />
            </div>
            <studyteam-button
                v-else-if="props.reifyLocationId"
                :micrositeId="reifyId"
                :trialId="reifyTrialId"
                :sponsorsiteid="reifyLocationId"
                :siteId="props.reifyLocationId"
            />
        </div>

    </div>
</template>

<script>
    export default {
        name: "Location"
    }
</script>

<style scoped lang="scss">

</style>
